import { AccountType } from "@common/constants/domains/accounting/accounts";
import {
    compareScopeOrder,
    resolveInsertIndex,
    ScopeInsertPosition,
    ScopeMemberKind,
    ScopeOrderMember,
} from "@common/helpers/domains/accounting/chartScopeOrdering";
import {
    parseScaledSortOrder,
    planScopePlacement,
    ScaledScopeMember,
    ScopePlacedRow,
} from "@common/helpers/domains/accounting/chartScopeSortOrder";
import { DraftAccount, DraftGroup } from "./draftState";

// Within a (type, parentGroupId) scope accounts and groups share ONE ordering
// space (a single sequence), so a custom group can be interleaved between
// accounts rather than always sorting after them. The order itself is
// `compareScopeOrder`, the same total order the server's placement reads, so a
// gesture and the row it persists cannot disagree on where a tie lands.
export type { ScopeInsertPosition };

interface CombinedScope {
    accounts: DraftAccount[];
    groups: DraftGroup[];
    entityId: number;
    type: AccountType;
    parentGroupId: string | null;
}

// The scope's accounts + groups as one ordered list, each member tagged with
// its kind so a change can be written back to the right array.
export const orderedScopeMembers = ({
    accounts,
    groups,
    entityId,
    type,
    parentGroupId,
}: CombinedScope): ScopeOrderMember[] => {
    const members: ScopeOrderMember[] = [
        ...accounts
            .filter(
                (account) =>
                    account.entityId === entityId &&
                    account.type === type &&
                    account.parentGroupId === parentGroupId,
            )
            .map((account) => ({
                id: account.id,
                kind: "account" as const,
                sortOrder: account.sortOrder,
                name: account.name,
            })),
        ...groups
            .filter(
                (group) =>
                    group.entityId === entityId &&
                    group.type === type &&
                    group.parentGroupId === parentGroupId,
            )
            .map((group) => ({
                id: group.id,
                kind: "group" as const,
                sortOrder: group.sortOrder,
                name: group.name,
            })),
    ];
    return members.sort(compareScopeOrder);
};

// Combined ordered ids for a scope (accounts + groups), used by the move ops to
// splice a dragged row into the unified sequence.
export const orderedScopeIdsInScope = (params: CombinedScope): string[] =>
    orderedScopeMembers(params).map((member) => member.id);

// Append position for a new row in its scope: one past the highest existing
// sortOrder across BOTH accounts and groups there, or 0 when the scope is empty.
export const nextSortOrderInScope = (params: CombinedScope): number => {
    const sortOrders = orderedScopeMembers(params).map(
        (member) => member.sortOrder,
    );
    if (sortOrders.length === 0) {
        return 0;
    }
    return Math.max(...sortOrders) + 1;
};

export type DraftPlacedRow = ScopePlacedRow;

export type DraftSortOrderAssignments = Map<string, number>;

interface PlanDraftScopePlacementParams {
    // May still contain the movers (moves read live state).
    members: ScopeOrderMember[];
    placed: DraftPlacedRow[];
    // Remaining siblings before the slot, not an index in a list that still includes movers.
    insertIndex: number;
}

// Same helper persist uses, so a tie lands in the same slot.
export const planDraftScopePlacement = ({
    members,
    placed,
    insertIndex,
}: PlanDraftScopePlacementParams): DraftSortOrderAssignments => {
    const placedIds = new Set(placed.map((row) => row.id));
    const siblings: ScaledScopeMember[] = members
        .filter((member) => !placedIds.has(member.id))
        .map((member) => ({
            id: member.id,
            kind: member.kind,
            name: member.name,
            // String(1e-7) is "1e-7"; BigInt cannot parse it.
            sortOrder: parseScaledSortOrder(member.sortOrder.toFixed(10)),
        }));
    const placement = planScopePlacement({ siblings, placed, insertIndex });
    if (placement.status === "unrepresentable") {
        throw new Error(
            "Chart scope placement is unrepresentable: too many rows to respace.",
        );
    }
    return new Map(
        placement.assignments.map((assignment) => [
            assignment.id,
            Number.parseFloat(assignment.sortOrder),
        ]),
    );
};

export const applyDraftSortOrders = <
    T extends { id: string; sortOrder: number },
>(
    rows: T[],
    assignments: DraftSortOrderAssignments,
): T[] =>
    rows.map((row) => {
        const next = assignments.get(row.id);
        return next !== undefined ? { ...row, sortOrder: next } : row;
    });

interface PlaceNewRowInDraftScopeParams extends CombinedScope {
    position: ScopeInsertPosition;
    placed: DraftPlacedRow;
    nextAccounts: DraftAccount[];
    nextGroups: DraftGroup[];
}

export const placeNewRowInDraftScope = ({
    accounts,
    groups,
    entityId,
    type,
    parentGroupId,
    position,
    placed,
    nextAccounts,
    nextGroups,
}: PlaceNewRowInDraftScopeParams): {
    accounts: DraftAccount[];
    groups: DraftGroup[];
} => {
    const members = orderedScopeMembers({
        accounts,
        groups,
        entityId,
        type,
        parentGroupId,
    });
    const assignments = planDraftScopePlacement({
        members,
        placed: [placed],
        insertIndex: resolveInsertIndex({
            orderedIds: members.map((member) => member.id),
            position,
        }),
    });
    return {
        accounts: applyDraftSortOrders(nextAccounts, assignments),
        groups: applyDraftSortOrders(nextGroups, assignments),
    };
};

export type { ScopeMemberKind };
